import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useFocusEffect, useRoute } from '@react-navigation/native';

import { HeaderLogo } from 'assets/images';

import gStyles from 'config/styles';
import { LOGO_TEST_ID } from 'config/testID';

import { SCREEN } from 'router/screens';

import { selectEpgPlayerFullscreen } from 'store/epg/selectors';
import { selectMainMenuItemsById } from 'store/railsBuilder/selectors';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import usePrevious from 'utils/hooks/usePrevious';
import { isWeb } from 'utils/responsiveness';

import styles from './styles';

interface RootNavigationHeaderLeftProps {
    insets?: boolean;
    disableFocusOnMount?: boolean;
}

const RootNavigationHeaderLeft = ({
    insets = true,
    disableFocusOnMount,
}: RootNavigationHeaderLeftProps) => {
    const { t } = useTranslation();
    const route = useRoute();
    const menuItemsById = useSelector(selectMainMenuItemsById);
    const epgPlayerInFullscreen = useSelector(selectEpgPlayerFullscreen);

    const logoRef = useRef(null);

    const prevEpgPlayerInFullscreen = usePrevious(epgPlayerInFullscreen);

    const accessibilityLabel = useMemo(() => {
        // @ts-ignore
        const id = route?.params?.id;
        const name = route?.name;

        if (menuItemsById[id]) {
            return t(menuItemsById[id].POEditorKey);
        }

        if (name === SCREEN.EPG) {
            return t('MODULE_MAIN_MENU_TV');
        }

        return '';
        // @ts-ignore
    }, [menuItemsById, route?.name, route?.params?.id, t]);

    useFocusEffect(
        useCallback(() => {
            if (!disableFocusOnMount) {
                setTimeout(() => {
                    setAccessibilityFocusTo(logoRef);
                }, 200);
            }
        }, [disableFocusOnMount]),
    );

    useEffect(() => {
        if (prevEpgPlayerInFullscreen && !epgPlayerInFullscreen) {
            setTimeout(() => {
                setAccessibilityFocusTo(logoRef);
            }, 100);
        }
    }, [epgPlayerInFullscreen]);

    return (
        <View
            style={[
                gStyles.rootHeaderDefaultPaddingLeft,
                gStyles.headerContainer,
                styles.container,
            ]}
        >
            <View
                ref={logoRef}
                style={[styles.logo, !insets && styles.logoWithoutInsets]}
                accessible
                accessibilityLabel={accessibilityLabel}
                accessibilityRole="image"
                role="img"
                accessibilityLiveRegion="assertive"
                {...(isWeb && { tabIndex: -1 })}
            >
                <HeaderLogo testID={LOGO_TEST_ID} width={49} height={24} />
            </View>
        </View>
    );
};

export default RootNavigationHeaderLeft;
